import type {
  ChiliPiperMeeting,
  ContactFormFields,
  ContactFormMetadata,
} from '@transcend-io/contact-form-schema';
import type { BrowserAnalytics } from '@transcend-io/segment/browser';
import type { TrackingEvent } from '@transcend-io/segment/core';
import { snakeCase } from 'change-case';

import { submitLead } from '../contact-form-client/submit';

type ScheduleAfterSubmitArgs = {
  fields: ContactFormFields;
  submissionId: string;
  analyticsId: string;
  trackingEventName: TrackingEvent;
  analytics: BrowserAnalytics;
  getMetadata: (recaptchaToken?: string) => Promise<ContactFormMetadata>;
  executeRecaptcha?: ((action: string) => Promise<string>) | undefined;
  initSchedulingModal: (fields: ContactFormFields, analyticsId: string) => void;
  waitForSchedulingFinished: () => Promise<ChiliPiperMeeting | null>;
};

/**
 * Opens Chili Piper, waits for book/close/DQ, and persists any booked meeting
 * onto the lead submission.
 */
export async function scheduleAfterSubmit({
  fields,
  submissionId,
  analyticsId,
  trackingEventName,
  analytics,
  getMetadata,
  executeRecaptcha,
  initSchedulingModal,
  waitForSchedulingFinished,
}: ScheduleAfterSubmitArgs): Promise<ChiliPiperMeeting | null> {
  const schedulingFields: ContactFormFields = {
    ...fields,
    submissionId,
    consent: true,
  };

  initSchedulingModal(schedulingFields, analyticsId);

  const chiliPiperMeeting = await waitForSchedulingFinished();
  if (!chiliPiperMeeting?.eventId) {
    return chiliPiperMeeting;
  }

  const meetingRecaptchaToken =
    executeRecaptcha && (await executeRecaptcha(snakeCase(trackingEventName)));
  const meetingMetadata = await getMetadata(meetingRecaptchaToken);

  const meetingResponse = await submitLead(
    {
      email: fields.email,
      submissionId,
      consent: true,
      chiliPiperMeeting,
    },
    meetingMetadata,
    { analytics, isSubmissionComplete: false },
  );

  if ('error' in meetingResponse) {
    console.error(
      'Failed to persist ChiliPiper meeting on submission',
      meetingResponse.error,
    );
  }

  return chiliPiperMeeting;
}
